import {
  Box,
  Button,
  CardActions,
  CardContent,
  Chip,
  IconButton,
  InputAdornment,
  TextField as MuiTextField,
  Tooltip,
  Typography,
} from "@mui/material";
import React, { useEffect, useRef, useState } from "react";
import Loading from "../../../../components/loading/loading";
import {
  AddRounded,
  ArrowBackRounded,
  ContentCopyRounded,
  EditRounded,
  SearchRounded,
  VisibilityRounded,
} from "@mui/icons-material";
import { useNavigate, useSearchParams } from "react-router-dom";
import { getChoicesList } from "../../../../services/choices/choices_services";
import { useQuery } from "react-query";
import { create_choice } from "../../../../routes/routes_path";
import locales from "../../../../constants/locales";
import useUser from "../../../../providers/user/user_provider";
import styled from "@emotion/styled";
import SortIcon from "../../../../assets/sort.svg";
import FilterIcon from "../../../../assets/filter_icon.svg";
import FilterChoicesForChoice from "../filter_choices/filter_choice_for_choices";
import { useSurveysFilterStore } from "../../../../store/use-surveys-filter-store";
import { BehaviorSubject, debounceTime } from "rxjs";
import { useActiveFilterCount } from "../../../../store/use-surveys-filter-store";
import { black, blue, blueLight, green, greenLight, purple, purpleLight } from "../../../../theme/colors";
import { ToolbarButton } from "../../../../components/toolbar_button/toolbar_button";
import {
  TableCell,
  TableEmptyState,
  TableFooterPagination,
  TableHeaderRow,
  TableRow,
} from "../../../users/components/table_toolkit/table_toolkit";
import SortChoicesPopup, {
  DEFAULT_CHOICES_SORT_OPTION,
  ChoicesSortOption,
  choicesSortOptionLabel,
  choicesSortOptionToParams,
} from "../sort_choices_popup/sort_choices_popup";
import OrgSelector from "../../../../components/org_selector/org_selector";
import { useSelectedOrgStore } from "../../../../store/use-selected-org-store";

const ROWS_PER_PAGE_OPTIONS = [8, 25, 50, 100];

const TextField = styled(MuiTextField)(() => ({
  "& .MuiOutlinedInput-root": {
    backgroundColor: "#FFFFFF",
    borderRadius: 8,
    "& fieldset": { borderColor: "#ABA0AB" },
    "&:hover fieldset": { borderColor: purple },
    "&.Mui-focused fieldset": { borderColor: purple, borderWidth: 2 },
  },
}));

interface ChoiceListItem {
  id: number;
  name: string;
  languages: string[];
  validFrom: string;
  validTo: string;
  updatedAt: string;
  createdAt: string;
  totalQuestions: number;
  choiceId: number;
}

export default function ChoicesListing() {
  const { startDate, endDate, validFrom, validTo, setOpenChoicesModal, searchValue, setSearchValue } = useSurveysFilterStore()
  const activeFilterCount = useActiveFilterCount()
  const [debouncedSearch, setDebouncedSearch] = useState(searchValue);
  const searchSubjectRef = useRef(new BehaviorSubject<string>(debouncedSearch)); // persist subject

  const [searchParams, setSearchParams] = useSearchParams();
  const [page, setPage] = useState(
    searchParams.has("page") ? parseInt(searchParams?.get("page")!) : 1
  );
  const [pageSize, setPageSize] = useState(ROWS_PER_PAGE_OPTIONS[0]);
  const [sortOption, setSortOption] = useState<ChoicesSortOption>(DEFAULT_CHOICES_SORT_OPTION);
  const [sortDialogOpen, setSortDialogOpen] = useState(false);
  const { selectedOrgId } = useSelectedOrgStore();

  const navigate = useNavigate();
  const { hasPermission, userPermissions } = useUser();

  const handleChangePage = (
    event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number
  ) => {
    setSearchParams({ page: (newPage + 1).toString() })
    setPage(newPage + 1);
  };

  const { data: choicesListData, isLoading } = useQuery(
    ["choicesListing", page, pageSize,
      searchValue,
      startDate,
      endDate,
      validFrom,
      validTo,
      sortOption,
      selectedOrgId,
    ],
    async () => {
      const { sortBy, sortOrder } = choicesSortOptionToParams(sortOption);
      let response = await getChoicesList({
        keyword: searchValue,
        page: page, size: pageSize,
        dateFrom: startDate,
        dateTo: endDate,
        validFrom: validFrom,
        validTo: validTo,
        sortBy,
        sortOrder,
        orgId: selectedOrgId,
      });
      return response?.data?.data || [];
    },
    {
      retry: false
    }
  );
  const startRange = (page - 1) * pageSize + 1;
  const endRange = Math.min((page) * pageSize, choicesListData?.total || 0);

  const customText = `Showing ${choicesListData?.total ? startRange : 0} to ${endRange || 0} of ${choicesListData?.total || 0} entries`;

  const list: ChoiceListItem[] = choicesListData?.list || [];

  useEffect(() => {
    const subscription = searchSubjectRef.current.pipe(debounceTime(500)).subscribe((val) => {
      setSearchValue(val)
    })

    return () => { subscription.unsubscribe() }; // cleanup
  }, []);


  useEffect(() => {
    setSearchParams({ page: (page).toString() });
  }, [page, setSearchParams]);
  useEffect(() => {
    const currentPageParam = Number(searchParams.get("page")) || 1;
    if (page !== currentPageParam) {
      setSearchParams(prev => {
        const params = new URLSearchParams(prev);
        params.set("page", (page).toString());
        return params;
      }, { replace: true });
    }
  }, [page, searchParams, setSearchParams]);

  const handleBackClick = () => {
    const currPage = Number(searchParams.get("page")) || 1;

    if (currPage > 1) {
      const newPage = currPage - 1;
      setPage(newPage);
    }
  };

  return (
    <Box display={"flex"} flexDirection={"column"} sx={{ height: "100%" }}>
      <CardContent sx={{ p: { xs: 2.5, sm: 3 } }}>
          {userPermissions?.superAdmin && page > 1 && (
            <Box mb={1}>
              <Tooltip title="Back to previous page">
                <IconButton onClick={handleBackClick} size="small">
                  <ArrowBackRounded fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          )}

          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            flexWrap="wrap"
            gap={2}
            mb={2}
          >
            <Box maxWidth={{ xs: "100%", sm: 360 }} flex={1} minWidth={{ xs: "100%", sm: 220 }}>
              <TextField
                value={debouncedSearch}
                onChange={(e) => {
                  setPage(1)
                  setSearchParams({ page: '1' }, { replace: true })
                  setDebouncedSearch(e.target.value)
                  searchSubjectRef.current.next(e.target.value);
                }}
                size="small"
                fullWidth
                placeholder="Search Surveys"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchRounded sx={{ color: "text.primary" }} />
                    </InputAdornment>
                  ),
                }}
              />
            </Box>
            <Box display="flex" alignItems="center" gap={1.5} flexWrap="wrap">
              <OrgSelector lockToOwnOrg />
              <ToolbarButton
                icon={SortIcon}
                label={choicesSortOptionLabel(sortOption)}
                onClick={() => setSortDialogOpen(true)}
              />
              <ToolbarButton
                icon={FilterIcon}
                label="Filters"
                badgeCount={activeFilterCount}
                onClick={() => setOpenChoicesModal(true)}
              />
              {hasPermission("choices", "create") && (
                <Button
                  onClick={() => navigate("/surveys/createNewSurvey/")}
                  variant="contained"
                  disableElevation
                  startIcon={<AddRounded sx={{ height: 18, width: 18 }} />}
                  sx={{
                    bgcolor: purple,
                    borderRadius: 2,
                    textTransform: "none",
                    fontWeight: 600,
                    "&:hover": { bgcolor: "#7A2074" },
                  }}
                >
                  Create New Survey
                </Button>
              )}
            </Box>
          </Box>

          {isLoading ? (
            <Box display="flex" justifyContent="center" py={8}>
              <Loading />
            </Box>
          ) : list.length === 0 ? (
            <TableEmptyState
              message="No surveys found"
              hint="Try adjusting your search or filters, or create a new survey."
            />
          ) : (
            <Box mt={1}>
              <TableHeaderRow
                labels={[
                  { label: "Sl. No.", flex: 0.3 },
                  { label: "Survey ID", flex: 0.4 },
                  { label: "Survey", flex: 1.9 },
                  { label: "Valid From", flex: 0.7 },
                  { label: "Valid To", flex: 0.7 },
                  { label: "Actions" },
                ]}
                lastColumnWidth={220}
                align="left"
                bordered
              />
              {list.map((choice, index) => (
                <TableRow key={choice.id} bordered>
                  <TableCell label="Sl. No." flex={0.3} bordered>
                    <Typography fontSize={14} color={black} noWrap>
                      {(page - 1) * pageSize + index + 1}
                    </Typography>
                  </TableCell>
                  <TableCell label="Survey ID" flex={0.4} bordered>
                    <Typography fontSize={14} color={black} noWrap>
                      {choice?.id}
                    </Typography>
                  </TableCell>
                  <TableCell label="Survey" flex={1.9} bordered>
                    <Typography fontWeight={600} color={black} noWrap>
                      {choice?.name}
                    </Typography>
                    <Box display="flex" flexDirection="row" flexWrap="wrap" alignItems="center" mt={0.25} gap={0.75}>
                      <Typography variant="body2" color={purple} fontWeight={600}>
                        {`Questions: ${choice.totalQuestions}`}
                      </Typography>
                      {choice?.languages?.map((item: string) => (
                        <Chip
                          key={item}
                          size="small"
                          label={locales?.find((locale) => locale?.code === item)?.name || item}
                          sx={{ bgcolor: purpleLight, color: purple, fontWeight: 600, textTransform: "capitalize" }}
                        />
                      ))}
                    </Box>
                  </TableCell>
                  <TableCell label="Valid From" flex={0.7} bordered>
                    <Typography fontSize={14} color={black} noWrap>
                      {choice?.validFrom || "-"}
                    </Typography>
                  </TableCell>
                  <TableCell label="Valid To" flex={0.7} bordered>
                    <Typography fontSize={14} color={black} noWrap>
                      {choice?.validTo || "-"}
                    </Typography>
                  </TableCell>
                  <TableCell isLast lastColumnWidth={220} bordered>
                    <Box display="flex" flexWrap="wrap" justifyContent={{ xs: "flex-start", sm: "flex-end" }} alignItems="center" gap={0.5}>
                      <Tooltip title="View survey">
                        <IconButton
                          onClick={() => navigate(`/surveys/viewSurveys/${choice?.id}`)}
                          sx={{ bgcolor: greenLight, "&:hover": { bgcolor: "#D6EEE0" } }}
                        >
                          <VisibilityRounded sx={{ color: green, fontSize: 20 }} />
                        </IconButton>
                      </Tooltip>
                      {hasPermission("choices", "update") && (
                        <Tooltip title="Edit survey">
                          <IconButton
                            onClick={() => navigate(`/surveys/editSurvey/${choice?.id}`)}
                            sx={{ bgcolor: purpleLight, "&:hover": { bgcolor: "#F0D9F0" } }}
                          >
                            <EditRounded sx={{ color: purple, fontSize: 20 }} />
                          </IconButton>
                        </Tooltip>
                      )}
                      {hasPermission("choices", "create") && (
                        <Tooltip title="Copy to create new survey">
                          <IconButton
                            onClick={() => navigate(create_choice, { state: { id: choice?.id } })}
                            sx={{ bgcolor: blueLight, "&:hover": { bgcolor: "#DCE6F3" } }}
                          >
                            <ContentCopyRounded sx={{ color: blue, fontSize: 20 }} />
                          </IconButton>
                        </Tooltip>
                      )}
                      <Button
                        variant="outlined"
                        onClick={() =>
                          navigate(`/surveys/viewSurvey/${choice?.id}`, {
                            state: { id: choice?.choiceId },
                          })
                        }
                        sx={{
                          borderColor: purple,
                          color: purple,
                          borderRadius: 2,
                          textTransform: "none",
                          fontWeight: 600,
                          whiteSpace: "nowrap",
                          "&:hover": { borderColor: purple, bgcolor: purpleLight },
                        }}
                      >
                        View responses
                      </Button>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </Box>
          )}
        </CardContent>

        {list.length > 0 && (
          <CardActions sx={{ px: 3, pb: 3, mt: "auto" }}>
            <Box width="100%">
              <TableFooterPagination
                count={choicesListData?.total || 0}
                page={page - 1}
                onPageChange={handleChangePage}
                summaryText={customText}
                rowsPerPage={pageSize}
                rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
                onRowsPerPageChange={(newSize) => {
                  setPageSize(newSize);
                  setPage(1);
                }}
              />
            </Box>
          </CardActions>
        )}

      <SortChoicesPopup
        open={sortDialogOpen}
        onClose={() => setSortDialogOpen(false)}
        sortOption={sortOption}
        setSortOption={setSortOption}
      />
      <FilterChoicesForChoice />
    </Box>
  );
}
